import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Box,
  AspectRatio,
  Text,
  Icon,
  Button,
} from "@chakra-ui/react";
import { FaPlay, FaArrowRight } from "react-icons/fa";
import { motion } from "framer-motion";

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Handle different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/, // Standard and shortened URLs
    /youtube\.com\/embed\/([^&\s?]+)/, // Embed URLs
    /youtube\.com\/shorts\/([^&\s?]+)/, // YouTube Shorts
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      // Remove any query parameters from the video ID
      return match[1].split('?')[0];
    }
  }
  
  return null;
};

const MotionButton = motion(Button);

export const IntroVideoDrawer = ({ isOpen, onClose, videoUrl, propertyName }) => {
  const [videoId, setVideoId] = useState(null);
  const [isShorts, setIsShorts] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [hasWatched, setHasWatched] = useState(false);
  const playerRef = useRef(null);

  useEffect(() => {
    if (videoUrl) {
      const id = getYouTubeVideoId(videoUrl);
      setVideoId(id);
      setIsShorts(videoUrl.includes('/shorts/'));
      
      // Check if user has watched this video before
      const storageKey = `intro_video_watched_${id}`;
      const watched = localStorage.getItem(storageKey) === 'true';
      setHasWatched(watched);
      if (watched) {
        setVideoEnded(true);
      }
    }
  }, [videoUrl]);

  // Load YouTube Iframe API
  useEffect(() => {
    if (!videoId) return;

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      return;
    }

    // Load the API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      console.log('YouTube Iframe API loaded');
    };
  }, [videoId]);

  // Initialize player when drawer opens
  useEffect(() => {
    if (!isOpen || !videoId || !window.YT || !window.YT.Player) return;

    const timer = setTimeout(() => {
      try {
        playerRef.current = new window.YT.Player('intro-video-player', {
          events: {
            onStateChange: (event) => {
              // YT.PlayerState.ENDED = 0
              if (event.data === 0) {
                console.log('Video ended');
                setVideoEnded(true);
                const storageKey = `intro_video_watched_${videoId}`;
                localStorage.setItem(storageKey, 'true');
                setHasWatched(true);
              }
            },
          },
        });
      } catch (error) {
        console.error('Error initializing YouTube player:', error);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [isOpen, videoId]);

  if (!videoId) {
    return null;
  }

  return (
    <Drawer
      isOpen={isOpen}
      placement="bottom"
      onClose={onClose}
      size="full"
    >
      <DrawerOverlay />
      <DrawerContent
        borderTopRadius="20px"
        maxH="80vh"
        bg="#1a202c"
        maxW={{ base: "100%", md: "640px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <DrawerHeader
          borderBottomWidth="1px"
          borderBottomColor="#2d3748"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          pr={4}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Icon as={FaPlay} color="#817efc" fontSize="16px" />
            <Text fontSize="16px" fontWeight="700" color="white">
              Welcome Video
            </Text>
          </Box>
          <Button
            onClick={onClose}
            size="sm"
            color="#a89fff"
            bg="rgba(129, 126, 255, 0.12)"
            fontSize="13px"
            fontWeight={600}
            borderRadius="8px"
            border="1px solid"
            borderColor="rgba(129, 126, 255, 0.3)"
            px={4}
            h="34px"
            _hover={{ bg: "rgba(129, 126, 255, 0.22)" }}
            _active={{ transform: "scale(0.97)" }}
            transition="all 0.15s ease"
          >
            Dismiss
          </Button>
        </DrawerHeader>

        <DrawerBody p={4} display="flex" flexDirection="column" overflow="hidden" position="relative">
          <Box
            flex="1"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            overflow="hidden"
          >
            {propertyName && (
              <Text
                fontSize="16px"
                fontWeight="600"
                color="gray.300"
                mb={3}
                textAlign="center"
              >
                {propertyName}
              </Text>
            )}
            
            <AspectRatio 
              ratio={isShorts ? 9 / 16 : 16 / 9} 
              w="100%" 
              maxW={isShorts ? "400px" : "min(800px, 90vw)"}
              maxH={isShorts ? "50vh" : "min(50vh, 400px)"}
            >
              <iframe
                id="intro-video-player"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&enablejsapi=1`}
                title="Property Welcome Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  borderRadius: "12px",
                  border: "none",
                }}
              />
            </AspectRatio>

            <Text
              fontSize="14px"
              fontWeight="500"
              color="gray.400"
              mt={4}
              textAlign="center"
              px={4}
            >
              {videoEnded ? "Ready to continue!" : "Watch this welcome video to learn more about your stay"}
            </Text>
          </Box>

          {/* Floating Continue Button */}
          {videoEnded && (
            <Box
              position="absolute"
              bottom="20px"
              left="50%"
              transform="translateX(-50%)"
              zIndex={10}
            >
              <MotionButton
                onClick={onClose}
                bg="#817efc"
                color="white"
                h="56px"
                px={8}
                borderRadius="28px"
                fontSize="18px"
                fontWeight="700"
                rightIcon={<Icon as={FaArrowRight} />}
                boxShadow="0 8px 24px rgba(129, 126, 252, 0.4)"
                _hover={{ bg: "#6c60fc", transform: "scale(1.05)" }}
                _active={{ transform: "scale(0.98)" }}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                Continue
              </MotionButton>
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};